import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import {
  FiCheck,
  FiX,
  FiArrowRight,
  FiRefreshCw,
  FiStar,
  FiShield,
  FiCreditCard,
  FiArrowLeft,
} from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import { DISPLAY_PLANS, WHATSAPP_SALES_URL } from "../../config/planAccess";
import VidalLogo from "../../imgs/logo.png";

const spin = keyframes`from { transform: rotate(0); } to { transform: rotate(360deg); }`;
const shimmer = keyframes`0% { transform: translateX(-120%); } 100% { transform: translateX(120%); }`;
const fadeUp = keyframes`from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); }`;

const formatReais = (v) => Number(v || 0).toLocaleString("pt-BR");

const PLAN_HEADINGS = {
  starter: "Starter",
  pro: "Profissional",
  enterprise: "Sob consulta",
};

const Screen = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100000;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  color: #0f172a;
  font-family: "Inter", "Segoe UI", system-ui, -apple-system, sans-serif;
  -webkit-font-smoothing: antialiased;
  background:
    radial-gradient(1200px 600px at 50% -10%, rgba(16, 185, 129, 0.06), transparent 60%),
    linear-gradient(180deg, #ffffff 0%, #f6f8f9 100%);
`;

const Nav = styled.header`
  flex-shrink: 0;
  width: 100%;
  background: #0b0d12;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);

  .inner {
    max-width: 1180px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 15px 32px;
  }

  img { height: 26px; width: auto; }

  .back {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.12);
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.8rem;
    font-weight: 500;
    font-family: inherit;
    padding: 8px 16px;
    border-radius: 999px;
    cursor: pointer;
    transition: all 0.18s ease;
    &:hover { color: #fff; border-color: rgba(255, 255, 255, 0.22); background: rgba(255, 255, 255, 0.1); }
  }
`;

const Wrap = styled.main`
  position: relative;
  z-index: 1;
  flex: 1;
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
  padding: 40px 32px 48px;
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: ${fadeUp} 0.55s cubic-bezier(0.16, 1, 0.3, 1) both;

  @media (max-width: 640px) { padding: 28px 20px 40px; }
`;

const Pill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.74rem;
  font-weight: 600;
  color: #047857;
  background: rgba(16, 185, 129, 0.08);
  border: 1px solid rgba(16, 185, 129, 0.2);
  padding: 7px 16px;
  border-radius: 999px;
  margin-bottom: 20px;
  svg { color: #10b981; }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 800;
  letter-spacing: -1.4px;
  line-height: 1.08;
  margin: 0 0 14px;
  color: #0f172a;
  text-align: center;

  .grad {
    background: linear-gradient(90deg, #10b981, #059669);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
  }

  @media (max-width: 640px) { font-size: 1.8rem; letter-spacing: -0.8px; }
`;

const Sub = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  max-width: 560px;
  margin: 0 auto 30px;
  color: #64748b;
  text-align: center;
  strong { color: #334155; font-weight: 600; }
`;

const Cycle = styled.div`
  display: inline-flex;
  align-items: center;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  padding: 4px;
  margin-bottom: 36px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);

  button {
    border: none;
    background: transparent;
    color: #64748b;
    padding: 9px 24px;
    border-radius: 999px;
    font-size: 0.86rem;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    transition: all 0.25s ease;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    &.active {
      background: #0f172a;
      color: #fff;
      box-shadow: 0 4px 12px -3px rgba(15, 23, 42, 0.3);
    }
    .save {
      display: inline-block;
      font-size: 0.64rem;
      margin-left: 8px;
      padding: 2px 8px;
      background: rgba(16, 185, 129, 0.15);
      color: #059669;
      border-radius: 999px;
      font-weight: 700;
    }
    &.active .save { background: rgba(16, 185, 129, 0.25); color: #6ee7b7; }
  }
`;

const Trust = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 28px;
  margin-top: 34px;
  padding-top: 26px;
  border-top: 1px solid #e2e8f0;
  width: 100%;
  max-width: 1120px;

  span { display: inline-flex; align-items: center; gap: 8px; font-size: 0.8rem; color: #64748b; font-weight: 500; }
  svg { color: #10b981; }
`;

const Foot = styled.p`
  font-size: 0.74rem;
  color: #94a3b8;
  margin-top: 16px;
  text-align: center;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  align-items: stretch;
  width: 100%;
  max-width: 1080px;
  margin: 0 auto;
  text-align: left;

  @media (max-width: 940px) {
    grid-template-columns: 1fr;
    max-width: 420px;
    gap: 18px;
  }
`;

const Card = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  border-radius: 20px;
  padding: 32px 28px 28px;
  background: #ffffff;
  transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), border-color 0.25s, box-shadow 0.3s;
  border: 1px solid
    ${({ $selected, $popular }) =>
      $selected ? "#10b981" : $popular ? "rgba(16, 185, 129, 0.35)" : "#e6e9ee"};
  box-shadow: ${({ $selected, $popular }) =>
    $selected
      ? "0 24px 50px -20px rgba(16, 185, 129, 0.32), 0 0 0 1px #10b981"
      : $popular
      ? "0 26px 56px -24px rgba(16, 185, 129, 0.28)"
      : "0 4px 16px -6px rgba(15, 23, 42, 0.06)"};
  transform: ${({ $popular }) => ($popular ? "translateY(-10px)" : "none")};

  &:hover {
    transform: ${({ $popular }) => ($popular ? "translateY(-14px)" : "translateY(-6px)")};
    border-color: #10b981;
    box-shadow: 0 28px 60px -24px rgba(16, 185, 129, 0.3);
  }

  @media (max-width: 940px) {
    transform: none;
    &:hover { transform: translateY(-4px); }
  }

  .ribbon {
    position: absolute;
    top: -13px;
    left: 50%;
    transform: translateX(-50%);
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: linear-gradient(135deg, #10b981, #059669);
    color: #fff;
    font-size: 0.64rem;
    font-weight: 700;
    letter-spacing: 0.6px;
    padding: 6px 16px;
    border-radius: 999px;
    text-transform: uppercase;
    box-shadow: 0 8px 20px -6px rgba(16, 185, 129, 0.6);
    overflow: hidden;
    white-space: nowrap;
    .shine {
      position: absolute;
      inset: 0;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
      animation: ${shimmer} 3.2s ease-in-out infinite;
    }
  }

  .head { display: flex; align-items: center; justify-content: space-between; }
  h3 { margin: 0; font-size: 1.2rem; font-weight: 800; color: #0f172a; letter-spacing: -0.3px; }
  .tag {
    font-size: 0.62rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #64748b;
    background: #f1f5f9;
    padding: 4px 10px;
    border-radius: 999px;
  }
  .desc { font-size: 0.82rem; line-height: 1.5; color: #94a3b8; margin: 10px 0 0; min-height: 40px; }

  .price { display: flex; align-items: baseline; gap: 3px; margin: 22px 0 0; min-height: 48px; }
  .price .cur { font-size: 0.9rem; font-weight: 700; color: #475569; align-self: flex-start; margin-top: 6px; }
  .price .amt { font-size: 2.7rem; font-weight: 800; letter-spacing: -2px; line-height: 1; color: #0f172a; }
  .price .per { font-size: 0.85rem; color: #94a3b8; font-weight: 500; }
  .price .consult { font-size: 1.7rem; font-weight: 800; color: #0f172a; letter-spacing: -0.5px; }
  .note { font-size: 0.72rem; color: #94a3b8; margin: 6px 0 0; min-height: 16px; }

  .divider { height: 1px; background: #eef2f6; margin: 22px 0 0; }

  ul { list-style: none; padding: 0; margin: 20px 0 0; display: grid; gap: 12px; flex: 1; }
  li { display: flex; align-items: flex-start; gap: 10px; font-size: 0.82rem; line-height: 1.35; color: #475569; }
  li.hl { color: #0f172a; font-weight: 600; }
  li .ic {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 999px;
    flex-shrink: 0;
    margin-top: 1px;
    background: rgba(16, 185, 129, 0.1);
    color: #10b981;
  }
  li.off { color: #cbd5e1; }
  li.off .ic { background: #f1f5f9; color: #cbd5e1; }
`;

const CardButton = styled.button`
  width: 100%;
  margin-top: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  padding: 13px 20px;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition: transform 0.2s ease, background 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  svg { flex-shrink: 0; }
  svg.spin { animation: ${spin} 1s linear infinite; }
  &:active { transform: scale(0.98); }
  &:disabled { opacity: 0.75; cursor: not-allowed; }

  ${({ $variant }) =>
    $variant === "primary"
      ? `
    background: linear-gradient(135deg, #10b981, #059669);
    color: #fff;
    border: none;
    box-shadow: 0 14px 30px -12px rgba(16, 185, 129, 0.7);
    &:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 18px 36px -12px rgba(16, 185, 129, 0.8); }
  `
      : $variant === "whatsapp"
      ? `
    background: #25d366;
    color: #fff;
    border: none;
    box-shadow: 0 14px 30px -12px rgba(37, 211, 102, 0.6);
    &:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 18px 36px -12px rgba(37, 211, 102, 0.7); }
    svg { font-size: 1.15rem; }
  `
      : `
    background: #fff;
    color: #0f172a;
    border: 1.5px solid #e2e8f0;
    &:hover:not(:disabled) { border-color: #10b981; color: #059669; background: rgba(16, 185, 129, 0.04); }
  `}
`;

export default function PlanSelectionStep({ onSelectPlan, onBack }) {
  const [cycle, setCycle] = useState("MONTHLY");
  const [selected, setSelected] = useState("pro");
  const [processing, setProcessing] = useState(null);

  const handleSelect = (plan) => {
    setSelected(plan.slug);
    setProcessing(plan.slug);
    if (plan.contact) {
      window.open(WHATSAPP_SALES_URL, "_blank", "noopener,noreferrer");
    }
    onSelectPlan(plan.slug);
  };

  return (
    <Screen>
      <Nav>
        <div className="inner">
          <img src={VidalLogo} alt="Vidal" />
          <button className="back" onClick={onBack}>
            <FiArrowLeft size={14} /> Voltar ao cadastro
          </button>
        </div>
      </Nav>

      <Wrap>
        <Pill>
          <FiCheck size={14} /> Escolha seu plano
        </Pill>

        <Title>
          Escolha o plano <span className="grad">ideal para você</span>
        </Title>

        <Sub>
          Seus dados e o certificado já estão prontos. Selecione um plano para
          liberar o acesso e <strong>começar a captura automática das suas notas</strong>.
        </Sub>

        <Cycle>
          <button className={cycle === "MONTHLY" ? "active" : ""} onClick={() => setCycle("MONTHLY")}>
            Mensal
          </button>
          <button className={cycle === "YEARLY" ? "active" : ""} onClick={() => setCycle("YEARLY")}>
            Anual <span className="save">-20%</span>
          </button>
        </Cycle>

        <Grid>
          {DISPLAY_PLANS.map((p) => {
            const isSel = selected === p.slug;
            const price = cycle === "YEARLY" ? p.priceYearly : p.priceMonthly;
            const isProc = processing === p.slug;
            const variant = p.contact
              ? "whatsapp"
              : p.popular || isSel
              ? "primary"
              : "ghost";
            return (
              <Card
                key={p.slug}
                $selected={isSel}
                $popular={p.popular}
                onClick={() => setSelected(p.slug)}
              >
                {p.popular && (
                  <span className="ribbon">
                    <FiStar size={11} /> Recomendado
                    <span className="shine" />
                  </span>
                )}
                <div className="head">
                  <h3>{PLAN_HEADINGS[p.slug] || p.name}</h3>
                  <span className="tag">{p.tagline}</span>
                </div>
                <p className="desc">{p.description}</p>
                <div className="price">
                  {price ? (
                    <>
                      <span className="cur">R$</span>
                      <span className="amt">{formatReais(price)}</span>
                      <span className="per">/mês</span>
                    </>
                  ) : (
                    <span className="consult">Sob consulta</span>
                  )}
                </div>
                <p className="note">
                  {price
                    ? cycle === "YEARLY"
                      ? "cobrado anualmente"
                      : `ou R$${formatReais(p.priceYearly)}/mês no anual`
                    : "valores personalizados"}
                </p>
                <div className="divider" />
                <ul>
                  {p.features.map((f) => (
                    <li
                      key={f.text}
                      className={`${f.included ? "" : "off"}${f.highlight ? " hl" : ""}`}
                    >
                      <span className="ic">
                        {f.included ? <FiCheck size={12} /> : <FiX size={12} />}
                      </span>
                      <span>{f.text}</span>
                    </li>
                  ))}
                </ul>
                <CardButton
                  $variant={variant}
                  disabled={processing !== null}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelect(p);
                  }}
                >
                  {isProc ? (
                    <>
                      <FiRefreshCw className="spin" size={16} /> Processando...
                    </>
                  ) : p.contact ? (
                    <>
                      <FaWhatsapp /> {p.ctaLabel || "Falar no WhatsApp"}
                    </>
                  ) : (
                    <>
                      Testar {PLAN_HEADINGS[p.slug] || p.name} <FiArrowRight size={16} />
                    </>
                  )}
                </CardButton>
              </Card>
            );
          })}
        </Grid>

        <Trust>
          <span>
            <FiShield size={15} /> Pagamento seguro via Mercado Pago
          </span>
          <span>
            <FiCreditCard size={15} /> Cartão, Pix ou boleto
          </span>
          <span>
            <FiCheck size={15} /> Cancele quando quiser
          </span>
        </Trust>

        <Foot>
          Ao continuar você concorda com os termos de uso e a política de
          privacidade da Vidal.
        </Foot>
      </Wrap>
    </Screen>
  );
}
